import { Delegate } from '@theme/delegate';
import { Form } from '@theme/form';

class PdpBuyButton extends HTMLElement {
  #strings = {};
  #delegate = null;
  #productComponent = null;
  #variant = null;
  #addedTimeout = null;
  #errorTimeout = null;
  #addToCartAbort = null;
  #isSubmitting = false;
  // Figma PDP flow: with no size selected the button still reads "Add to Cart";
  // the "Please, choose size" prompt appears on press (or desktop hover) and
  // only then sticks until a size is picked.
  #prompted = false;

  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    const stringsEl = this.querySelector('[data-ref="strings"]');
    this.#strings = JSON.parse(stringsEl?.textContent || '{}');

    this.#productComponent = this.closest('product-component');

    this.#delegate = new Delegate(this);
    this.#delegate.on('click', '[data-action="add-to-cart"]', this.#onAddClick);
    this.querySelector('form')?.addEventListener('submit', this.#onSubmit);

    const button = this.#getButton();
    button?.addEventListener('mouseenter', this.#onButtonHoverStart);
    button?.addEventListener('mouseleave', this.#onButtonHoverEnd);

    this.#productComponent?.addEventListener('variant:changed', this.#onVariantChanged);
    this.#productComponent?.addEventListener('notify-me:ready', this.#onNotifyMeReady);

    requestAnimationFrame(() => this.#initState());
  }

  disconnectedCallback() {
    this.#productComponent?.removeEventListener('variant:changed', this.#onVariantChanged);
    this.#productComponent?.removeEventListener('notify-me:ready', this.#onNotifyMeReady);
    this.#productComponent = null;
    this.querySelector('form')?.removeEventListener('submit', this.#onSubmit);

    const button = this.#getButton();
    button?.removeEventListener('mouseenter', this.#onButtonHoverStart);
    button?.removeEventListener('mouseleave', this.#onButtonHoverEnd);
    this.#delegate?.off();
    this.#delegate = null;
    this.#addToCartAbort?.abort();
    this.#addToCartAbort = null;
    clearTimeout(this.#addedTimeout);
    this.#addedTimeout = null;
    clearTimeout(this.#errorTimeout);
    this.#errorTimeout = null;
  }

  #onVariantChanged = (event) => {
    if (this.#isSubmitting) return;
    this.#updateButtonState(event.detail?.variant ?? null);
  };

  #onNotifyMeReady = () => {
    this.#initState();
  };

  #initState() {
    if (this.dataset.hasVariants === 'true') {
      this.#syncFromVariantInput();
      return;
    }

    const variantInput = this.querySelector('[data-ref="variant-id"]');
    const available = this.dataset.initialVariantAvailable === 'true';

    if (!variantInput?.value) {
      this.#updateButtonState(null);
      return;
    }

    this.#updateButtonState({
      id: Number(variantInput.value),
      available,
    });
  }

  #onSubmit = (event) => {
    event.preventDefault();
    this.#onAddClick(event);
  };

  #onAddClick = (event) => {
    event.preventDefault();
    this.#initState();

    const button = this.#getButton();

    if (!button || button.dataset.state === 'choose-size') {
      return;
    }

    if (button.dataset.state === 'no-selection') {
      this.promptSizeSelection();
      return;
    }

    if (button.dataset.state === 'notify-me') {
      this.#openNotifyMe();
      return;
    }

    if (button.dataset.state !== 'ready' || this.#isSubmitting) {
      return;
    }

    this.#addToCart();
  };

  #syncFromVariantInput() {
    if (this.dataset.hasVariants !== 'true') return;

    const variantSelects = this.closest('product-component')?.querySelector('variant-selects');
    const variants = JSON.parse(
      variantSelects?.querySelector('[data-ref="variants-json"]')?.textContent || '[]',
    );

    let variant = this.#findCurrentVariant(variantSelects, variants);

    if (!variant) {
      const variantInput = this.querySelector('[data-ref="variant-id"]');
      if (variantInput?.value) {
        variant = variants.find((item) => String(item.id) === variantInput.value) ?? null;
      }
    }

    this.#updateButtonState(variant);
  }

  #findCurrentVariant(variantSelects, variants) {
    if (!variantSelects) return null;

    /** @type {Record<string, string | null>} */
    const selected = {};

    variantSelects.querySelectorAll('.pdp-variant-option').forEach((fieldset) => {
      const position = fieldset.dataset.optionPosition;
      const checked = fieldset.querySelector('.pdp-variant-option__input:checked');
      selected[position] = checked?.value ?? null;
    });

    for (const fieldset of variantSelects.querySelectorAll('.pdp-variant-option')) {
      const position = fieldset.dataset.optionPosition;
      if (!selected[position]) return null;
    }

    return (
      variants.find((variant) =>
        variant.options.every((optionValue, index) => optionValue === selected[String(index + 1)]),
      ) ?? null
    );
  }

  #updateButtonState(variant) {
    if (this.#isSubmitting) return;

    this.#variant = variant;
    const isComingSoon = this.dataset.comingSoon === 'true';
    const hasVariants = this.dataset.hasVariants === 'true';
    const variantInput = this.querySelector('[data-ref="variant-id"]');

    if (!variantInput) return;

    if (variant?.id) {
      this.#prompted = false;
      variantInput.value = String(variant.id);

      if (isComingSoon && this.#hasNotifyMe()) {
        variantInput.disabled = true;
        this.#applyState('notify-me');
        this.#updateAvailabilityMessage(variant);
        return;
      }

      if (this.#isVariantAvailable(variant)) {
        variantInput.disabled = false;
        this.#applyState('ready');
        this.#updateAvailabilityMessage(variant);
        return;
      }

      variantInput.disabled = true;

      if (this.#hasNotifyMe()) {
        this.#applyState('notify-me');
      } else if (isComingSoon) {
        this.#applyState('coming-soon');
      } else {
        this.#applyState('sold-out');
      }

      this.#updateAvailabilityMessage(variant);
      return;
    }

    if (!hasVariants) {
      variantInput.disabled = true;
      this.#applyState('unavailable');
      this.#updateAvailabilityMessage(variant);
      return;
    }

    variantInput.value = '';
    variantInput.disabled = true;

    if (this.#prompted) {
      this.#applyState('choose-size', this.#getFirstUnselectedOptionName());
    } else {
      this.#applyState('no-selection');
    }

    this.#updateAvailabilityMessage(null);
  }

  promptSizeSelection({ scroll = true } = {}) {
    if (this.#isSubmitting) return;

    this.#prompted = true;
    this.#applyState('choose-size', this.#getFirstUnselectedOptionName());

    if (scroll) {
      // "PDP scrolls up, same as with the sticky ATC banner" — only moves when
      // the size options are actually off-screen (block: 'nearest').
      this.#productComponent
        ?.querySelector('variant-selects')
        ?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }

  #onButtonHoverStart = () => {
    if (!window.matchMedia('(hover: hover)').matches) return;

    const button = this.#getButton();
    if (button?.dataset.state !== 'no-selection') return;

    this.#setButtonText(button, this.#getChooseText(this.#getFirstUnselectedOptionName()));
  };

  #onButtonHoverEnd = () => {
    const button = this.#getButton();
    if (button?.dataset.state !== 'no-selection') return;

    this.#setButtonText(button, this.#strings.addToCart);
  };

  #getFirstUnselectedOptionName() {
    const variantSelects = this.closest('product-component')?.querySelector('variant-selects');
    if (!variantSelects) return null;

    // Options render in position order; name the first one the customer hasn't resolved
    // so the label guides them through (size -> color) instead of always saying "size".
    for (const fieldset of variantSelects.querySelectorAll('.pdp-variant-option')) {
      if (!fieldset.querySelector('.pdp-variant-option__input:checked')) {
        return fieldset.dataset.optionName || null;
      }
    }

    return null;
  }

  #updateAvailabilityMessage(variant) {
    const messageEl = this.querySelector('[data-ref="availability-message"]');
    if (!messageEl) return;

    // Any size/variant with no inventory shows the configurable restock message, not only a
    // coming-soon product (rule 5 of ticket 869e7w59f).
    const hasAvailabilityMessage = this.dataset.hasAvailabilityMessage === 'true';
    const show =
      hasAvailabilityMessage &&
      Boolean(variant?.id) &&
      !this.#isVariantAvailable(variant);

    messageEl.hidden = !show;
  }

  #addToCart() {
    this.#initState();

    const button = this.#getButton();
    const form = this.querySelector('form');
    const variantInput = this.querySelector('[data-ref="variant-id"]');

    if (!button || !form || !variantInput?.value) return;

    if (button.dataset.state === 'notify-me' || (this.#variant && !this.#isVariantAvailable(this.#variant))) {
      this.#openNotifyMe();
      return;
    }

    if (button.dataset.state !== 'ready' || variantInput.disabled) return;

    this.#isSubmitting = true;
    button.setAttribute('disabled', '');
    button.removeAttribute('aria-disabled');
    this.#setLoaderVisible(true);

    const formHelper = new Form();
    variantInput.removeAttribute('disabled');
    const serialized = formHelper.serialize(form);

    this.#addToCartAbort?.abort();
    this.#addToCartAbort = new AbortController();
    const { signal } = this.#addToCartAbort;

    fetch(`${window.routes.localeRootUrl}/cart/add.js`, {
      body: JSON.stringify(serialized),
      credentials: 'same-origin',
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      signal,
    })
      .then((response) => {
        if (signal.aborted) return;

        if (response.ok) {
          if (window.theme.cartMode === 'page') {
            window.location.href = window.routes.cart_page_url;
            return;
          }

          if (window.location.href.includes('cart')) {
            window.location.reload();
            return;
          }

          this.#setLoaderVisible(false);
          this.#applyState('added');

          this.dispatchEvent(
            new CustomEvent('product:added', {
              bubbles: true,
              detail: {
                variant: serialized.id,
                quantity: serialized.quantity,
              },
            }),
          );

          clearTimeout(this.#addedTimeout);
          this.#addedTimeout = setTimeout(() => {
            this.#isSubmitting = false;
            this.#updateButtonState(this.#variant);
          }, 800);

          return;
        }

        return response.json().then(() => {
          if (!signal.aborted) {
            this.#showError();
          }
        });
      })
      .catch((error) => {
        if (error?.name === 'AbortError' || signal.aborted) return;
        this.#showError();
      });
  }

  #showError() {
    const button = this.#getButton();
    const errorEl = this.querySelector('[data-ref="error"]');

    this.#isSubmitting = false;
    this.#setLoaderVisible(false);
    button?.removeAttribute('disabled');
    this.#updateButtonState(this.#variant);

    if (errorEl) {
      errorEl.classList.add('visible');
      clearTimeout(this.#errorTimeout);
      // timeout: auto-hide cart error banner after brief display
      this.#errorTimeout = setTimeout(() => {
        errorEl.classList.remove('visible');
        this.#errorTimeout = null;
      }, 2500);
    }
  }

  #getChooseText(optionName = null) {
    if (this.dataset.giftCard === 'true') {
      return this.#strings.chooseAmount;
    }

    if (optionName && this.#strings.chooseOption) {
      return this.#strings.chooseOption.replace('[option]', optionName.toLowerCase());
    }

    return this.#strings.chooseSize;
  }

  #applyState(state, optionName = null) {
    const button = this.#getButton();
    if (!button) return;

    const textByState = {
      'choose-size': this.#getChooseText(optionName),
      'no-selection': this.#strings.addToCart,
      ready: this.#strings.addToCart,
      'sold-out': this.#strings.soldOut,
      'notify-me': this.#strings.notifyMe,
      'coming-soon': this.#strings.comingSoon,
      unavailable: this.#strings.unavailable,
      added: this.#strings.added,
    };

    const text = textByState[state] ?? '';
    this.#setButtonText(button, text);
    button.dataset.state = state;

    if (state === 'choose-size') {
      button.removeAttribute('disabled');
      button.setAttribute('aria-disabled', 'true');
    } else {
      button.removeAttribute('aria-disabled');

      if (state === 'ready' || state === 'notify-me' || state === 'no-selection') {
        button.removeAttribute('disabled');
      } else {
        button.setAttribute('disabled', '');
      }
    }

    // Dispatch after disabled/aria settle so the sticky ATC mirrors the final state.
    this.#productComponent?.dispatchEvent(
      new CustomEvent('buy-button:state-changed', {
        bubbles: true,
        detail: { state },
      }),
    );
  }

  #setButtonText(button, text) {
    button.querySelectorAll('.button-txt').forEach((el) => {
      el.textContent = text;
    });
    button.setAttribute('aria-label', text);
  }

  #setLoaderVisible(visible) {
    const button = this.#getButton();
    button?.classList.toggle('is-loading', visible);
    button?.querySelector('.itemLoader')?.classList.toggle('is-visible', visible);

    // Loading is not a data-state, so announce it separately — the sticky ATC
    // syncs its is-loading/disabled mirror from this event.
    this.#productComponent?.dispatchEvent(
      new CustomEvent('buy-button:state-changed', {
        bubbles: true,
        detail: { state: button?.dataset.state },
      }),
    );
  }

  #getButton() {
    return (
      this.querySelector('[data-ref="buy-button"]') ??
      this.querySelector('.pdp-buy-button__btn') ??
      this.querySelector('[data-action="add-to-cart"]')
    );
  }

  #hasNotifyMe() {
    if (this.dataset.notifyMe === 'true') return true;
    if (this.#productComponent?.dataset.hasNotifyMe === 'true') return true;
    return Boolean(this.#productComponent?.querySelector('pdp-notify-me'));
  }

  #isVariantAvailable(variant) {
    if (!variant?.id) return false;

    const inventoryQuantity = Number(variant.inventory_quantity);
    const hasTrackedInventory = Boolean(variant.inventory_management);
    if (hasTrackedInventory && Number.isFinite(inventoryQuantity) && inventoryQuantity < 1) {
      return false;
    }

    return Boolean(variant.available);
  }

  #openNotifyMe() {
    this.#productComponent?.querySelector('pdp-notify-me')?.open?.();
  }
}

if (!customElements.get('pdp-buy-button')) {
  customElements.define('pdp-buy-button', PdpBuyButton);
}
